import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import Tooltip from 'components/Tooltip';
import { Property } from ".";

import styles from './properties.module.scss';
import StoryboardContext from 'components/Storyboard/context';
import { MediaLibraryModal } from 'components/MediaLibrary';
import { SECTION_COLUMN, MENU } from "constants/widgetTypes";
import { EditorContext } from '../../index';
import { updateWidgetProperty } from 'actions/editor';

import { DEFAULT_COLOR, DEFAULT_MEDIA_PROPERTY, POSITION, SCALE } from 'constants/background';
import DropShadow from './dropShadow';

const Background = ({ value, onChange, showShadow }) => {
  const type = _.get(value, 'type');

  return (
    <div className={styles.backgroundProperty}>
      <BackgroundType value={value} onChange={onChange} />
      {(type === 'image' || type === 'video') &&
        <Background.MediaProperty value={value} onChange={onChange} shadow={showShadow} />
      }
    </div>
  )
};

const BackgroundType = ({ value, onChange }) => {
  const targetRef = React.useRef(null);
  const storyboardContext = React.useContext(StoryboardContext);
  const type = _.get(value, 'type', '');
  const color = type === 'gradient' || type === 'solid' ? value : _.get(value, 'color') || DEFAULT_COLOR;

  const editorContext = React.useContext(EditorContext);
  const entityType = _.get(editorContext, 'entityType', null);

  const [state, setState] = useState({
    color: false,
    image: false,
    video: false
  });

  const showColorDialog = () => {
    setState({
      color: true,
      image: false,
      video: false
    });
  }

  const showImageLibrary = () => {
    setState({
      color: false,
      image: true,
      video: false
    });
  }

  const showVideoLibrary = () => {
    setState({
      color: false,
      image: false,
      video: true
    });
  }

  const handleColorChange = (color) => {
    var request = {
      type:'color',
      color: color
    }
    onChange(request);
  }

  const handleImageChange = (media) => {
    var existing = value || DEFAULT_MEDIA_PROPERTY;
    
    const type = _.get(existing, 'type');
    if(type != 'image'){
      existing = DEFAULT_MEDIA_PROPERTY;
    } 

    var change = {
      ...existing,
      type: 'image',
      media: media[0]
    }

    onChange(change);
    closePopup();
  }

  const handleVideoChange = (media) => {
    var existing = value || DEFAULT_MEDIA_PROPERTY;
    
    const type = _.get(existing, 'type');
    if(type != 'video'){
      existing = DEFAULT_MEDIA_PROPERTY;
    } 

    var change = {
      ...existing,
      type: 'video',
      media: media[0]
    }

    onChange(change);
    closePopup();
  }

  const handleNoneChange = () => {
    onChange(null);
  }

  const closePopup = () => {
    setState({
      color: false,
      image: false,
      video: false
    });
  }

  return (
    <>
      <Property name="Background Type" className={styles.backgroundTypesProperty}>
        <div ref={targetRef} className={styles.backgroundTypes}>
          <Tooltip placement="top" content="Color">
            <button className={cx(styles.type, {
              [styles.active]: (type === 'color' || type === 'gradient' || type === 'solid')
            })} onClick={showColorDialog}>
              <i className={cx('cleverstory-font-color')}></i>
            </button>
          </Tooltip>
          <Tooltip placement="top" content="Image">
            <button className={cx(styles.type, {
              [styles.active]: type === 'image'
            })} onClick={showImageLibrary}>
              <i className={cx('cleverstory-background')}></i>
            </button>
          </Tooltip>
          <Tooltip placement="top" content="Video">
            <button className={cx(styles.type, {
              [styles.active]: type === 'video'
            })} onClick={showVideoLibrary}>
              <i className={cx('cleverstory-video')}></i>
            </button>
          </Tooltip>
          <Tooltip placement="top" content="None">
            <button className={cx(styles.type, {
              [styles.active]: type == null
            })} onClick={handleNoneChange}>
              <i className={cx('cleverstory-none')}></i>
            </button>
          </Tooltip>
        </div>
      </Property>

      {state.color &&
        <Property.ColorPickerDialog
          target={targetRef.current}
          colorConfig={color}
          value={color}
          gradient={true}
          onChange={handleColorChange}
          onCancel={closePopup}
        />
      }

      {state.image &&
        <MediaLibraryModal
          show={state.image}
          storyboard={storyboardContext}
          mediaTypes={['image']}
          multiple={false}
          onSelect={handleImageChange}
          onClose={() => { closePopup() }}
          onChange = {onChange}
          target= {_.includes(["live_element", "palette_component"], entityType) ? {
            id: editorContext.editorSession.id.split('_')[1],
            type: entityType
          } : null 
        }
        />
      }

      {state.video &&
        <MediaLibraryModal
          show={state.video}
          storyboard={storyboardContext}
          mediaTypes={['video']}
          multiple={false}
          onSelect={handleVideoChange}
          onClose={() => {closePopup()}}
          target= {_.includes(["live_element", "palette_component"], entityType) ? {
            id: editorContext.editorSession.id.split('_')[1],
            type: entityType
          } : null 
        }
        />
      }
    </>
  );
}

Background.Type = BackgroundType;

const MediaBackgroundProperty = ({ value, onChange }) => {
  const type = _.get(value, 'type');
  const opacity = _.get(value, 'opacity', 1);
  const color = _.get(value, 'color') || DEFAULT_COLOR;
  const scale = _.get(value, 'scale', 'scale_to_fit');
  const position = _.get(value, 'position', 'center');

  const typeLabel = type === 'image' ? 'Image' : 'Video';

  const handleOpacityChange = (opacity) => {
    const change = {
      ...value,
      opacity: opacity
    };

    onChange(change);
  }

  const handleColorChange = (color) => {
    const change = {
      ...value,
      color: color
    };

    onChange(change);
  }

  const handleScaleChange = (scale) => {
    const change = {
      ...value,
      scale: scale
    };

    onChange(change);
  }

  const handlePositionChange = (position) => {
    const change = {
      ...value,
      position: position
    };

    onChange(change);
  }

  return (
    <>
      <Property name={`${typeLabel} Opacity`}>
        <Property.Slider className={styles.opacityProperty}
          min={1}
          max={100}
          input
          value={opacity}
          onChange={handleOpacityChange}
        />
      </Property>
      <Property.Divider />
      <Property.Color title={`Color Behind ${typeLabel}`} value={color} onChange={handleColorChange} />
      <div className={styles.scalingPositionProperty}>
        {type === 'image' &&
          <div className={styles.scalingContainer}>
            <Property name="Scaling" className={styles.scalingProperty}>
              <div className={styles.body}>
                <Property.RadioButton className={styles.radio}
                  title='Scale to fit'
                  value={scale == SCALE.SCALE_TO_FILL}
                  onClick={() => handleScaleChange(SCALE.SCALE_TO_FILL)}
                />
                <Property.RadioButton className={styles.radio}
                  title='Tiled'
                  value={scale == SCALE.TILED}
                  onClick={() => handleScaleChange(SCALE.TILED)}
                />
              </div>
            </Property>
          </div>
        }
        <div className={styles.positionContainer}>
          <Property name="Position" className={styles.positionProperty}>
            <div className={styles.body}>
              {Object.keys(POSITION).map(key => {
                const value = POSITION[key];
                const active = position === value;

                return (
                  <button className={cx({
                    [styles.active]: active
                  })} onClick={() => handlePositionChange(value)}
                  >
                    <i className={cx(value === POSITION.CENTER ? "cleverstory-plus" : "cleverstory-back")} />
                  </button>
                );
              })}
            </div>
          </Property>
        </div>
      </div>
    </>
  )
}

Background.MediaProperty = MediaBackgroundProperty;

const BackgroundDialog = ({ id, value, onChange, onCancel, widgetProps, showShadow }) => {

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`));
  const widgetproperties = _.find(widgets, (widget) => widget.uid == id);
  const shadowProperties = _.get(widgetproperties, `properties.${breakpoint}.shadow`, {});
  
  
  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleShadowChange = ( field, value) => {
    handlePropertyChange({
      shadow: {
        ...shadowProperties,
        [field]: value
      }
    });
  }

  return (
    <Property.Dialog className={styles.backgroundDialog}
      width={246}
      target={`#${id}`}
      title={'Background'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Background value={value} onChange={onChange} />
      </Property.Body>

      {showShadow &&
        <DropShadow id={id} properties={shadowProperties} handleShadowChange={handleShadowChange}/>
      }

    </Property.Dialog>
  );
}

Background.Dialog = BackgroundDialog;

export { Background };